import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../lib/api";

const ProfileContext = createContext(null);
const STORAGE_KEY = "fitsme_profile_id_v1";

export const ProfileProvider = ({ children }) => {
    const [profileId, setProfileId] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(false);

    const refresh = useCallback(async (id) => {
        const targetId = id || profileId;
        if (!targetId) {
            setProfile(null);
            return null;
        }
        setLoading(true);
        try {
            const r = await api.get(`/profiles/${targetId}`);
            setProfile(r.data);
            return r.data;
        } catch {
            // Profile no longer exists — clear local state
            localStorage.removeItem(STORAGE_KEY);
            setProfileId(null);
            setProfile(null);
            return null;
        } finally {
            setLoading(false);
        }
    }, [profileId]);

    useEffect(() => {
        if (profileId && !profile) refresh(profileId);
    }, [profileId, profile, refresh]);

    const saveProfileId = useCallback((id) => {
        if (id) {
            localStorage.setItem(STORAGE_KEY, id);
            setProfileId(id);
        } else {
            localStorage.removeItem(STORAGE_KEY);
            setProfileId(null);
            setProfile(null);
        }
    }, []);

    const signOut = useCallback(() => saveProfileId(null), [saveProfileId]);

    const value = useMemo(
        () => ({ profileId, profile, loading, refresh, saveProfileId, signOut, setProfile }),
        [profileId, profile, loading, refresh, saveProfileId, signOut],
    );

    return (
        <ProfileContext.Provider value={value}>
            {children}
        </ProfileContext.Provider>
    );
};

export const useProfile = () => useContext(ProfileContext);
